import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { toast } from 'react-toastify'

import { AppDispatch, RootState, store } from 'store'
import { getErrorMessage } from 'utils/helpers'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { addContact } from 'services/api/creator-contacts'
import { AddContactPayload, Contact } from 'types/creators'
import { resetCreatorsState } from 'slices/creators/reducer'
import { platforms } from 'constants/global'
import instagramPlatformLogo from 'assets/images/platforms/platform-instagram.png'
import youtubePlatformLogo from 'assets/images/platforms/platform-youtube.png'
import TagsSelector from './TagsSelector'
import './AddCreatorContactModal.scss'

const platformLogos = {
    ig: instagramPlatformLogo,
    yt: youtubePlatformLogo,
}

type FormErrors = { platform?: string; handle?: string }

const AddCreatorContactModal: React.FC<{
    isOpen: boolean
    onClose: () => void
    onSuccess?: (contact: Contact) => void
}> = ({ isOpen, onClose, onSuccess }) => {
    const [processing, setProcessing] = useState(false)
    const dispatch = useDispatch<AppDispatch>()
    const [errors, setErrors] = useState<FormErrors>({})
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId)

    const [data, setData] = useState<AddContactPayload>({
        platform: 'ig',
        handle: '',
        tags: [],
    })

    const validateForm = () => {
        const newErrors: FormErrors = {}
        if (!data.platform) {
            newErrors.platform = 'Please select a social media platform.'
        }
        if (!data.handle) {
            newErrors.handle = 'Please enter the creator handle or profile link.'
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validateForm() || !selectedAgencyId) return
        try {
            setProcessing(true)
            const response = await addContact(selectedAgencyId, data)
            toast.success('Creator contact added successfully.')
            dispatch(resetCreatorsState())
            dispatch(fetchContactsThunk({ force: true, filters: store.getState().creators.filters }))
            if(response.contact) {
                onSuccess?.(response.contact)
            }
            onClose()
        } catch (error: unknown) {
            toast.error(getErrorMessage(error) || 'Failed to add creator. Please try again.')
        } finally {
            setProcessing(false)
        }
    }

    useEffect(() => {
        if (isOpen) {
            setData({ platform: 'ig', handle: '', tags: [] })
            setErrors({})
        }
    }, [isOpen])

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered className="add-creator-contact-modal">
            <ModalHeader toggle={onClose}>Add Creator</ModalHeader>
            <ModalBody>
                <p className="modal-subtitle">
                    Select a platform and enter the creator&apos;s handle or profile link.
                </p>
                <div className="add-creator-contact-form">
                    <div className="mb-3">
                        <label className="form-label">
                            Platform <span className="text-danger">*</span>
                        </label>
                        <div className="logo-radio-tile-group">
                            {Object.values(platforms).map((platform) => (
                                <div
                                    key={platform.id}
                                    className={`logo-radio-tile cursor-pointer ${data.platform === platform.id ? 'active' : ''}`}
                                    onClick={() => {
                                        setData({ ...data, platform: platform.id })
                                        setErrors((prev) => ({ ...prev, platform: undefined }))
                                    }}
                                    role="radio"
                                    aria-checked={data.platform === platform.id}
                                >
                                    <img
                                        className="h-100"
                                        src={platformLogos[platform.id as keyof typeof platformLogos]}
                                        alt={platform.name}
                                    />
                                </div>
                            ))}
                        </div>
                        {errors.platform && <div className="text-danger field-error">{errors.platform}</div>}
                    </div>
                    <div className="mb-3">
                        <label htmlFor="creator-handle" className="form-label">
                            Handle / Profile Link <span className="text-danger">*</span>
                        </label>
                        <input
                            id="creator-handle"
                            value={data.handle}
                            onBlur={validateForm}
                            onChange={(e) => {
                                setData({ ...data, handle: e.target.value })
                                setErrors((prev) => ({ ...prev, handle: undefined }))
                            }}
                            type="text"
                            className="form-control"
                            name="handle"
                            placeholder="@username or profile URL"
                        />
                        {errors.handle && <div className="text-danger field-error">{errors.handle}</div>}
                    </div>
                    <div className="mb-0">
                        <label htmlFor="creator-tags" className="form-label">
                            Tags
                        </label>
                        <TagsSelector value={data.tags} onChange={(tags) => setData({ ...data, tags })} />
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="theme-btn btn-primary-outlined" onClick={onClose}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="theme-btn btn-primary"
                    onClick={handleSubmit}
                    disabled={processing || !data.platform || !data.handle}
                >
                    {processing ? 'Adding...' : 'Add Creator'}
                </button>
            </ModalFooter>
        </Modal>
    )
}

export default AddCreatorContactModal
